import React, { useEffect, useState } from "react";
import { fetchSheetRows } from "./utils/fetchSheetRows";
import { formatDate, formatDateWithTime, SHEET_URL } from "./constants";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { parseDMYDate } from "./utils/parseDMYDate";

function StudentsTable({ user, selectedSchoolName, onLogout }) {
  const [students, setStudents] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const navigate = useNavigate();
  const selectedSchools = (Array.isArray(selectedSchoolName) ? selectedSchoolName : [selectedSchoolName || user.schoolName]).filter(Boolean);
  const schoolsForData = selectedSchools.length > 0 ? selectedSchools : [user.schoolName].filter(Boolean);

  // Sorting logic
  const sortedStudents = React.useMemo(() => {
    if (!sortConfig.key) return students;
    const sorted = [...students].sort((a, b) => {
      let valA = a[sortConfig.key] ?? "";
      let valB = b[sortConfig.key] ?? "";
      if (sortConfig.key === "volume") {
        valA = Number(valA);
        valB = Number(valB);
      } else {
        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();
      }
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [students, sortConfig]);

  // Function to get usage color based on percentage (same logic as 15-day usage)
  const getUsageColor = (percentage) => {
    if (percentage < 25) return "#F44336"; // Red
    if (percentage <= 40) return "#FFC107"; // Yellow
    return "#4CAF50"; // Green
  };

  useEffect(() => {
    async function fetchData() {
      const rows = await fetchSheetRows(SHEET_URL);
      const filtered = rows.filter(
        r => schoolsForData.includes(r.schoolName) &&
          r.type?.toLowerCase() === "student" &&
          r.name && r.name.trim() !== ""
      );
      setStudents(filtered);
    }
    fetchData();
  }, [selectedSchools.join("|")]);



  // Calculate stats for students
  const now = new Date();
  const activeStudents = students.filter(t => t.status && t.status.trim().toLowerCase() === "active");
  const activePercentage = students.length > 0 ? Math.round((activeStudents.length / students.length) * 100) : 0;

  const stats = {
    total: students.length,
    active: activeStudents.length,
    activePercentage: activePercentage,
    notActivated: students.filter(t => !t.status || t.status.trim() === "" || t.status.trim().toLowerCase() === "pending").length,
    deactivated: students.filter(t => t.status && (t.status.trim().toLowerCase() === "inactive" || t.status.trim().toLowerCase() === "deactivated")).length,
    usedIn15Days: students.filter(t => {
      if (!t.last_used_on) return false;
      const d = parseDMYDate(t.last_used_on);
      if (!d || isNaN(d.getTime())) return false;
      const diff = (now - d) / (1000 * 60 * 60 * 24);
      return diff <= 15;
    }).length
  };

  const colorTotal = "#FF5722";
  const colorActive = "#009688"; // Back to original color
  const colorNotActivated = "#FFC107";
  const colorDeactivated = "#9E9E9E";
  const colorUsed = "#3F51B5";

  const cardBaseStyle = {
    background: "#fff",
    borderRadius: "4px",
    boxShadow: "0 2px 8px rgba(38,61,117,0.08)",
    padding: "2rem 2.5rem",
    minWidth: 200,
    textAlign: "center",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    margin: 0
  };

  const chartOptions = {
    chart: { type: "column", backgroundColor: "#fff" },
    title: { text: "Students Overview", style: { color: "#263D75" } },
    xAxis: {
      categories: [
        "Total",
        "Active",
        "Not Activated",
        "Deactivated", // Changed from "Inactive" to "Deactivated"
        "Used in 15 Days"
      ],
      title: { text: null },
      labels: { style: { color: "#263D75", fontWeight: "bold" } }
    },
    yAxis: {
      min: 0,
      title: { text: "Count", style: { color: "#263D75" } },
      labels: { style: { color: "#263D75" } }
    },
    series: [
      {
        name: "Count",
        data: [
          { y: stats.total, color: colorTotal },
          { y: stats.active, color: colorActive },
          { y: stats.notActivated, color: colorNotActivated },
          { y: stats.deactivated, color: colorDeactivated },
          { y: stats.usedIn15Days, color: colorUsed }
        ]
      }
    ],
    legend: { enabled: false },
    credits: { enabled: false }
  };

  const exportAsPDF = async () => {
    const doc = new jsPDF("p", "pt", "a4");
    let y = 30;

    doc.setFontSize(20);
    doc.setTextColor("#263D75");
    doc.text("Students Overview", 40, y);

    y += 30;
    doc.setFontSize(12);
    const cardData = [
      { label: "Total Students", value: stats.total, color: "#FF5722" },
      { label: "Total Active Students", value: stats.active, color: colorActive },
      { label: "Total Not Activated", value: stats.notActivated, color: "#FFC107" },
      { label: "Total Inactive", value: stats.deactivated, color: "#9E9E9E" },
      { label: "Used in Last 15 Days", value: stats.usedIn15Days, color: "#3F51B5" }
    ];
    let cardX = 40;
    cardData.forEach(card => {
      doc.setFillColor(card.color);
      doc.rect(cardX, y, 90, 50, "F");
      doc.setTextColor("#fff");
      doc.setFontSize(16);
      doc.text(String(card.value), cardX + 45, y + 22, { align: "center" });
      doc.setFontSize(9);
      doc.text(card.label, cardX + 45, y + 40, { align: "center" });
      cardX += 100;
    });

    const chartEl = document.querySelector(".students-chart-pdf");
    if (chartEl) {
      const canvas = await html2canvas(chartEl, { backgroundColor: "#fff", scale: 2 });
      const imgData = canvas.toDataURL("image/png");
      y += 70;
      doc.addImage(imgData, "PNG", 40, y, 480, 220);
      y += 230;
    } else {
      y += 80;
    }

    const tableColumn = [
      "Name", "Type", "Standard", "Volume", "License_key", "Password", "Status", "Last Used on", "DOB"
    ];
    const tableRows = students.map(row => [
      row.name,
      row.type,
      row.standard,
      row.volume,
      row.license,
      row.password,
      row.status && row.status.trim() ? row.status : "Not Activated",
      row.last_used_on,
      row.dob
    ]);
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: y,
      styles: { font: "poppins", fontSize: 9 }
    });

    doc.save("Students_Overview.pdf");
  };

  const handleCardClick = (type) => {
    navigate(`/students/${type}`);
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#f6fbff",
      fontFamily: "'Poppins', 'Roboto', sans-serif",
      overflowY: "auto"
    }}>
      <header
        style={{
          width: "100%",
          background: "linear-gradient(90deg, #263D75 60%, #4F6CB6 100%)",
          color: "#fff",
          padding: "1rem 2rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxSizing: "border-box",
          boxShadow: "0 4px 16px 0 rgba(38,61,117,0.18), 0 1.5px 4px 0 rgba(38,61,117,0.10)",
          borderBottomLeftRadius: "18px",
          borderBottomRightRadius: "18px",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            background: "#fff",
            color: "#263D75",
            border: "none",
            borderRadius: "8px",
            padding: "0.4rem 1.1rem",
            fontWeight: "bold",
            cursor: "pointer",
            fontSize: "1rem",
            boxShadow: "0 2px 8px rgba(38,61,117,0.10)",
            marginRight: 18,
            transition: "background 0.2s, box-shadow 0.2s",
            outline: "none",
            display: "flex",
            alignItems: "center"
          }}
        >
          &#8592; Back
        </button>
        <div style={{
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          fontWeight: "bold",
          fontSize: "1.3rem",
          letterSpacing: "1px",
          color: "#fff",
          textAlign: "center",
          pointerEvents: "none",
          minWidth: 200,
          maxWidth: "40vw",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}>
          Students Overview
        </div>
        {onLogout && (
          <button
            onClick={onLogout}
            style={{
              background: "linear-gradient(90deg, #fff 60%, #eaf4ff 100%)",
              color: "#263D75",
              border: "none",
              borderRadius: "8px",
              padding: "0.55rem 1.4rem",
              fontWeight: "bold",
              cursor: "pointer",
              fontSize: "1rem",
              boxShadow: "0 2px 8px rgba(38,61,117,0.10)",
              transition: "background 0.2s, box-shadow 0.2s",
              outline: "none",
            }}
          >
            Logout
          </button>
        )}
      </header>
      <div style={{ padding: "2rem" }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "0.4rem"
        }}>
          <h1 style={{ color: "#263D75", margin: 0 }}>
            Students Overview
          </h1>
          <button
            onClick={exportAsPDF}
            style={{
              background: "#263D75",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: "0.5rem 1.2rem",
              fontWeight: "bold",
              cursor: "pointer",
              fontSize: "1rem",
              marginLeft: "1.2rem"
            }}
          >
            Export as PDF
          </button>
        </div>
        <p style={{
          color: "#4F6CB6",
          fontWeight: 400,
          fontSize: "1.08rem",
          margin: 0,
          marginBottom: "1.2rem",
          lineHeight: 1.5
        }}>
          Here’s a quick overview of all students, their activation status, and recent activity for your school.
        </p>
        <div
          style={{
            marginTop: "2rem",
            display: "grid",
            gridTemplateColumns: "260px 1fr",
            gap: "2rem",
            alignItems: "stretch",
            minHeight: 360
          }}
        >
          {/* Stats Cards */}
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            height: "100%",
            justifyContent: "stretch"
          }}>
            {/* Total Students Card */}
            <div onClick={() => handleCardClick("all")} style={{ ...cardBaseStyle, borderLeft: `6px solid ${colorTotal}`, cursor: "pointer" }}>
              <div style={{ fontSize: "2.2rem", color: colorTotal, fontWeight: "bold" }}>{stats.total}</div>
              <div style={{ color: colorTotal, fontWeight: "bold" }}>Total Students</div>
            </div>
            {/* Active Students Card */}
            <div onClick={() => handleCardClick("active")} style={{ ...cardBaseStyle, borderLeft: `6px solid ${colorActive}`, cursor: "pointer" }}>
              <div style={{ fontSize: "2.2rem", color: colorActive, fontWeight: "bold" }}>{stats.active}</div>
              <div style={{ color: colorActive, fontWeight: "bold" }}>Total Active Students</div>
              <div style={{ marginTop: "0.5rem", fontWeight: "bold", color: colorActive }}>{students.length > 0 ? `${stats.activePercentage}%` : "0%"}</div>
            </div>
            {/* Not Activated Students Card */}
            <div onClick={() => handleCardClick("not-activated")} style={{ ...cardBaseStyle, borderLeft: `6px solid ${colorNotActivated}`, cursor: "pointer" }}>
              <div style={{ fontSize: "2.2rem", color: colorNotActivated, fontWeight: "bold" }}>{stats.notActivated}</div>
              <div style={{ color: colorNotActivated, fontWeight: "bold" }}>Total Not Activated</div>
              <div style={{ marginTop: "0.5rem", fontWeight: "bold", color: colorNotActivated }}>{students.length > 0 ? `${Math.round(stats.notActivated / students.length * 100)}%` : "0%"}</div>
            </div>
            {/* Deactivated Students Card */}
            <div onClick={() => handleCardClick("deactivated")} style={{ ...cardBaseStyle, borderLeft: `6px solid ${colorDeactivated}`, cursor: "pointer" }}>
              <div style={{ fontSize: "2.2rem", color: colorDeactivated, fontWeight: "bold" }}>{stats.deactivated}</div>
              <div style={{ color: colorDeactivated, fontWeight: "bold" }}>Total Deactivated</div>
              <div style={{ marginTop: "0.5rem", fontWeight: "bold", color: colorDeactivated }}>{students.length > 0 ? `${Math.round(stats.deactivated / students.length * 100)}%` : "0%"}</div>
            </div>
            {/* Used in Last 15 Days Card */}
            <div onClick={() => handleCardClick("used-in-15-days")} style={{ ...cardBaseStyle, borderLeft: `6px solid ${colorUsed}`, cursor: "pointer" }}>
              <div style={{ fontSize: "2.2rem", color: colorUsed, fontWeight: "bold" }}>{stats.usedIn15Days}</div>
              <div style={{ color: colorUsed, fontWeight: "bold" }}>Used in Last 15 Days</div>
              <div style={{ marginTop: "0.5rem", fontWeight: "bold", color: colorUsed }}>{students.length > 0 ? `${Math.round(stats.usedIn15Days / students.length * 100)}%` : "0%"}</div>
            </div>
          </div>
          {/* Bar Chart */}
          <div className="students-chart-pdf" style={{ background: "#fff", borderRadius: "4px", boxShadow: "0 2px 8px rgba(38,61,117,0.08)", display: "block", minHeight: 340, width: "100%", height: "100%", padding: 0, margin: 0 }}>
            <HighchartsReact highcharts={Highcharts} options={chartOptions} containerProps={{ style: { width: "100%", height: "100%" } }} style={{ width: "100%", height: "100%" }} />
          </div>
        </div>
        {/* Data Table */}
        <div style={{
          marginTop: "0.5rem",
          background: "#fff",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(38,61,117,0.08)",
          padding: "1.5rem",
          overflowX: "auto"
        }}>
          <table style={{
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: "'Poppins', 'Roboto', sans-serif",
            fontSize: "1rem"
          }}>
            <thead>
              <tr style={{ background: "#eaf4ff" }}>
                <th style={{ padding: "0.7rem", textAlign: "left", borderRadius: "6px 0 0 6px", cursor: "pointer" }}
                  onClick={() => setSortConfig(sortConfig.key === "name" ? { key: "name", direction: sortConfig.direction === "asc" ? "desc" : "asc" } : { key: "name", direction: "asc" })}>
                  Name {sortConfig.key === "name" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                </th>
                <th style={{ padding: "0.7rem", textAlign: "left" }}>Type</th>
                <th style={{ padding: "0.7rem", textAlign: "left", cursor: "pointer" }}
                  onClick={() => setSortConfig(sortConfig.key === "standard" ? { key: "standard", direction: sortConfig.direction === "asc" ? "desc" : "asc" } : { key: "standard", direction: "asc" })}>
                  Standard {sortConfig.key === "standard" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                </th>
                <th style={{ padding: "0.7rem", textAlign: "left", cursor: "pointer" }}
                  onClick={() => setSortConfig(sortConfig.key === "volume" ? { key: "volume", direction: sortConfig.direction === "asc" ? "desc" : "asc" } : { key: "volume", direction: "asc" })}>
                  Volume {sortConfig.key === "volume" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                </th>
                <th style={{ padding: "0.7rem", textAlign: "left" }}>License_key</th>
                <th style={{ padding: "0.7rem", textAlign: "left" }}>Password</th>
                <th style={{ padding: "0.7rem", textAlign: "left", cursor: "pointer" }}
                  onClick={() => setSortConfig(sortConfig.key === "status" ? { key: "status", direction: sortConfig.direction === "asc" ? "desc" : "asc" } : { key: "status", direction: "asc" })}>
                  Status {sortConfig.key === "status" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                </th>
                <th style={{ padding: "0.7rem", textAlign: "left" }}>Last Used on</th>
                <th style={{ padding: "0.7rem", textAlign: "left", borderRadius: "0 6px 6px 0" }}>DOB</th>
              </tr>
            </thead>
            <tbody>
              {sortedStudents.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ textAlign: "center", color: "#aaa", padding: "1.5rem" }}>
                    No students found for this school.
                  </td>
                </tr>
              )}
              {sortedStudents.map((row, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "0.6rem" }}>{row.name}</td>
                  <td style={{ padding: "0.6rem", textTransform: "capitalize" }}>{row.type}</td>
                  <td style={{ padding: "0.6rem" }}>{row.standard}</td>
                  <td style={{ padding: "0.6rem" }}>{row.volume}</td>
                  <td style={{ padding: "0.6rem" }}>{row.license}</td>
                  <td style={{ padding: "0.6rem" }}>{row.password}</td>
                  <td style={{ padding: "0.6rem" }}>
                    {
                      row.status && row.status.trim()
                        ? (row.status.trim().toLowerCase() === "inactive" ? "Deactivated" : row.status)
                        : "Not Activated"
                    }
                  </td>
                  <td style={{ padding: "0.6rem" }}>{row.last_used_on}</td>
                  <td style={{ padding: "0.6rem" }}>{row.dob}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default StudentsTable;